import { SignRequest } from '@bitte-ai/types';
import { ChevronsUpDown } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useEVMTransaction } from '../../../hooks/useEvmTransaction';
import { useChatState } from '../../../state/chat.state';
import { useAccount } from '../../../state/wallet.state';
import LoadingMessage from '../../chat/LoadingMessage';
import { TransactionResult } from '../../chat/transactions/TransactionResult';
import { Button } from '../../ui/button';

interface TxButtonsProps {
  tx?: SignRequest;
}

export function TxButtons({ tx }: TxButtonsProps) {
  const chatState = useChatState();
  const { evmAddress } = useAccount();
  const [isDeclined] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const textColor = 'white';
  const [showCode, setShowCode] = useState(false);

  // Get the current message ID from the last message in the chat
  const currentMessageId = useMemo(() => {
    const messages = chatState.messages;
    if (messages.length === 0) return '';
    return messages[messages.length - 1].id;
  }, [chatState.messages]);

  const {
    isLoading,
    transaction,
    handleSmartAction,
    setErrorMsg: setEvmErrorMsg,
    setTransaction,
  } = useEVMTransaction({
    evmData: tx,
    append: chatState.append,
    messageId: currentMessageId,
  });

  const handleApprove = async () => {
    try {
      await handleSmartAction();
    } catch (error) {
      setErrorMsg(
        error instanceof Error ? error.message : 'Transaction failed',
      );
      // Clear error state from transaction if needed
      if (transaction?.status === 'error') {
        setTransaction((prev) =>
          prev
            ? {
                ...prev,
                error: undefined,
              }
            : null,
        );
      }
    }
  };

  const handleExplain = () => {
    // Get chat state methods
    const { append } = chatState;

    // Request explanation
    append({
      id: crypto.randomUUID(),
      role: 'user',
      content: 'Can you explain this transaction in detail?',
    });
  };

  const handleFunctionCallClick = () => {
    setShowCode(!showCode);
  };

  const loadingIndicator = (
    <div className="bitte-p-4 bitte-text-center">
      <LoadingMessage color={textColor} />
      <p className="bitte-text-center bitte-text-sm">Transaction in progress</p>
    </div>
  );

  return (
    <div className="bitte-flex bitte-flex-col bitte-gap-2 bitte-w-full">
      {errorMsg || (transaction?.status === 'error' && transaction?.error) ? (
        <div className="bitte-flex bitte-flex-col bitte-items-center bitte-gap-4 bitte-px-6 bitte-pb-6 bitte-text-center bitte-text-sm">
          <p className="bitte-text-red-300">
            {transaction?.status === 'error' && transaction?.error
              ? transaction.error
              : `An error occurred trying to execute your transaction: ${errorMsg}.`}
          </p>
          <Button
            className="bitte-w-1/2"
            variant="outline"
            onClick={() => {
              setErrorMsg('');
              setEvmErrorMsg('');
              // Clear error state from transaction if needed
              if (transaction?.status === 'error') {
                setTransaction((prev) =>
                  prev
                    ? {
                        ...prev,
                        error: undefined,
                      }
                    : null,
                );
              }
            }}
          >
            Dismiss
          </Button>
        </div>
      ) : isLoading ? (
        loadingIndicator
      ) : null}

      {isDeclined ? (
        <div className="bitte-px-6 bitte-pb-6 bitte-text-center bitte-text-sm">
          Transaction declined!
        </div>
      ) : null}

      {transaction?.txHash && tx ? (
        <TransactionResult
          result={{
            evm: { txHash: transaction.txHash, chainId: tx.chainId },
          }}
          accountId={evmAddress || ''}
          textColor={textColor}
          success={transaction.status === 'success'}
        />
      ) : null}

      {!isLoading && !errorMsg && !transaction?.txHash && !isDeclined ? (
        <div className="bitte-flex bitte-flex-col bitte-gap-2 bitte-w-full">
          <div className="bitte-flex bitte-gap-2 sm:bitte-gap-4 bitte-flex-shrink-0">
            <Button
              variant="outline"
              className="bitte-bg-[#1a1a1a] bitte-border-0 bitte-text-[#848484] bitte-text-[10px] bitte-rounded-md bitte-hover:bitte-bg-[#2a2a2a] bitte-py-1 bitte-h-[24px] bitte-text-uppercase"
              onClick={handleFunctionCallClick}
            >
              Function Call{' '}
              <span className="ml-1">{<ChevronsUpDown size={10} />}</span>
            </Button>

            <Button
              variant="outline"
              className="bitte-bg-mb-black-50 bitte-border-0 bitte-text-[#848484] bitte-text-[10px] bitte-rounded-md bitte-hover:bitte-bg-[#2a2a2a] bitte-py-1 bitte-h-[24px] bitte-text-uppercase"
              onClick={handleExplain}
            >
              Explain
            </Button>
          </div>

          {showCode && (
            <div className="bitte-p-5 bitte-bg-[#1a1a1a] bitte-flex-shrink-0 bitte-w-full">
              <div className="bitte-flex bitte-items-center bitte-justify-between bitte-mb-2">
                <div className="bitte-text-[#B4B4B4] bitte-font-medium bitte-text-[10px] bitte-uppercase">
                  TOOL CALL
                </div>
                <div className="bitte-rounded-full bitte-bg-[#2a2a2a] bitte-px-2 bitte-py-1 bitte-flex bitte-items-center">
                  <span className="bitte-text-[#B4B4B4] bitte-text-[10px]">
                    generate-swap-transaction
                  </span>
                </div>
              </div>
              <div className="bitte-overflow-y-auto bitte-h-[165px] bitte-text-white bitte-font-medium bitte-pt-3">
                <pre className="bitte-text-xs bitte-font-mono bitte-whitespace-pre bitte-text-[#e6e6e6]">
                  <code>{JSON.stringify(tx, null, 2)}</code>
                </pre>
              </div>
            </div>
          )}

          <Button
            onClick={handleApprove}
            className="bitte-w-full bitte-mb-8 bitte-mt-2 bitte-text-base bitte-font-medium bitte-h-[47px]"
            disabled={isLoading}
            variant="success"
            size="lg"
          >
            {isLoading ? (
              <span className="animate-pulse">Processing...</span>
            ) : (
              'Approve Transaction'
            )}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
